import axios from 'axios';
import api from './api';
import type {
  ImportMatchStatus,
  ImportPreviewItem,
  ImportPreviewResponse,
  ImportResult,
  ImportResultItem,
} from '../types/import';

const importBase = (venueId: string, wineListId: string) =>
  `/admin/venue/${venueId}/wine-list/${wineListId}/import`;

// ── Error extraction ──────────────────────────────────────────────────────────
// The backend uses { error: { code, message } } for error responses (per spec).
// Map known import error codes to operator-friendly messages.

const IMPORT_ERROR_MESSAGES: Record<string, string> = {
  IMPORT_INVALID_FILE_TYPE: 'Unsupported file type. Please upload a .xlsx or .csv file.',
  IMPORT_EMPTY_FILE: 'The uploaded file is empty.',
  IMPORT_ROW_LIMIT_EXCEEDED: 'File exceeds the 200-row limit. Split the file and import in batches.',
  WINE_LIST_NOT_FOUND: 'Wine list not found.',
  IMPORT_INVALID_BODY: 'Import data is invalid. Please try previewing again.',
  IMPORT_MISSING_RESOLUTION: 'Some rows are missing a resolution. Please review and try again.',
};

export function extractImportError(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as Record<string, unknown> | undefined;
    // Spec envelope: { error: { code, message } }
    const code = (data?.['error'] as Record<string, unknown> | undefined)?.['code'];
    if (typeof code === 'string' && IMPORT_ERROR_MESSAGES[code]) {
      return IMPORT_ERROR_MESSAGES[code];
    }
    const backendMessage = (data?.['error'] as Record<string, unknown> | undefined)?.['message']
      ?? data?.['message'];
    if (typeof backendMessage === 'string' && backendMessage) {
      return backendMessage;
    }
    if (err.response?.status === 413) {
      return 'File is too large. Please reduce the file size and try again.';
    }
    if (err.response?.status === 403) {
      return 'You do not have permission to import into this wine list.';
    }
    if (err.response?.status === 404) {
      return 'Wine list not found.';
    }
  }
  if (err instanceof Error && err.message) return err.message;
  return 'An unexpected error occurred. Please try again.';
}

// ── Known valid values ────────────────────────────────────────────────────────

const VALID_MATCH_STATUSES = new Set<ImportMatchStatus>([
  'EXACT', 'POSSIBLE', 'NONE', 'DUPLICATE', 'FAILED',
]);

const VALID_RESULT_STATUSES = new Set<ImportResultItem['status']>([
  'created', 'skipped', 'failed',
]);

// ── Response normalizers ──────────────────────────────────────────────────────

function normalizePreviewItem(raw: unknown): ImportPreviewItem | null {
  if (!raw || typeof raw !== 'object') return null;
  const item = raw as Record<string, unknown>;

  // importRow is required — skip the row entirely if absent
  const importRow = item['importRow'];
  if (!importRow || typeof importRow !== 'object') return null;

  const row = importRow as Record<string, unknown>;
  const rowIndex = Number(row['rowIndex'] ?? item['rowIndex'] ?? 0);

  const rawMatchStatus = item['matchStatus'];
  const matchStatus: ImportMatchStatus =
    typeof rawMatchStatus === 'string' && VALID_MATCH_STATUSES.has(rawMatchStatus as ImportMatchStatus)
      ? (rawMatchStatus as ImportMatchStatus)
      : 'FAILED'; // unknown status surfaces in the UI as FAILED rather than disappearing

  const candidates = Array.isArray(item['candidates']) ? item['candidates'] : [];
  const warnings = Array.isArray(item['warnings'])
    ? (item['warnings'] as unknown[]).map(w => String(w))
    : [];

  return {
    rowIndex,
    importRow: {
      rowIndex,
      rawName: typeof row['rawName'] === 'string' ? row['rawName'] : '',
      producerName: row['producerName'] != null ? String(row['producerName']) : null,
      vintage: row['vintage'] != null ? String(row['vintage']) : null,
      price: row['price'] != null ? Number(row['price']) : null,
      notes: row['notes'] != null ? String(row['notes']) : null,
    },
    matchStatus,
    candidates,
    warnings,
    resolution: null,
  };
}

function normalizePreviewResponse(raw: unknown): ImportPreviewResponse {
  // Handles both direct payload and { data: ... } envelope shapes
  const payload = (raw as Record<string, unknown>)?.['data'] ?? raw;
  const p = (payload ?? {}) as Record<string, unknown>;

  const rawItems = Array.isArray(p['items']) ? p['items'] : [];
  const items: ImportPreviewItem[] = rawItems.flatMap((item: unknown) => {
    const normalized = normalizePreviewItem(item);
    return normalized ? [normalized] : [];
  });

  // Counts are recomputed from normalized items as a safety net;
  // backend-provided counts are used when present and numeric.
  const computedCounts = items.reduce(
    (acc, item) => { acc[item.matchStatus] = (acc[item.matchStatus] ?? 0) + 1; return acc; },
    {} as Record<string, number>,
  );

  return {
    wineListId: p['wineListId'] != null ? String(p['wineListId']) : '',
    totalRows: Number(p['totalRows'] ?? items.length),
    exactCount: Number(p['exactCount'] ?? computedCounts['EXACT'] ?? 0),
    possibleCount: Number(p['possibleCount'] ?? computedCounts['POSSIBLE'] ?? 0),
    noneCount: Number(p['noneCount'] ?? computedCounts['NONE'] ?? 0),
    duplicateCount: Number(p['duplicateCount'] ?? computedCounts['DUPLICATE'] ?? 0),
    failedCount: Number(p['failedCount'] ?? computedCounts['FAILED'] ?? 0),
    items,
  };
}

function normalizeResultItem(raw: unknown): ImportResultItem {
  const item = (raw ?? {}) as Record<string, unknown>;
  const rawStatus = item['status'];
  // Normalise to lowercase in case backend uses uppercase ('CREATED', 'SKIPPED', 'FAILED')
  const normStatus = typeof rawStatus === 'string' ? rawStatus.toLowerCase() : '';
  return {
    rowIndex: Number(item['rowIndex'] ?? 0),
    status: VALID_RESULT_STATUSES.has(normStatus as ImportResultItem['status'])
      ? (normStatus as ImportResultItem['status'])
      : 'failed',
    wineListItemId: item['wineListItemId'] != null ? String(item['wineListItemId']) : null,
    wineId: item['wineId'] != null ? String(item['wineId']) : null,
    wineCreated: Boolean(item['wineCreated']),
    producerCreated: Boolean(item['producerCreated']),
    reason: item['reason'] != null ? String(item['reason']) : null,
  };
}

function normalizeImportResult(raw: unknown): ImportResult {
  // Handles both direct payload and { data: ... } envelope shapes
  const payload = (raw as Record<string, unknown>)?.['data'] ?? raw;
  const p = (payload ?? {}) as Record<string, unknown>;

  const rawItems = Array.isArray(p['items']) ? p['items'] : [];

  return {
    totalRows: Number(p['totalRows'] ?? 0),
    created: Number(p['created'] ?? 0),
    skipped: Number(p['skipped'] ?? 0),
    failed: Number(p['failed'] ?? 0),
    newWinesCreated: Number(p['newWinesCreated'] ?? 0),
    newProducersCreated: Number(p['newProducersCreated'] ?? 0),
    items: rawItems.map(normalizeResultItem),
  };
}

// ── Service ───────────────────────────────────────────────────────────────────

const wineListImportService = {
  /**
   * Upload a file and receive a preview of matched rows.
   * No writes are performed by this call.
   * Route: POST /admin/venue/:venueId/wine-list/:wineListId/import/preview
   */
  previewImport: async (
    venueId: string,
    wineListId: string,
    file: File,
  ): Promise<ImportPreviewResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    // Do NOT set Content-Type manually — axios sets multipart/form-data with
    // the correct boundary automatically when the body is a FormData instance.
    const response = await api.post(
      `${importBase(venueId, wineListId)}/preview`,
      formData,
    );
    return normalizePreviewResponse(response.data);
  },

  /**
   * Submit resolved preview items and create WineListItem records.
   * This is the single write operation in the import flow.
   * Route: POST /admin/venue/:venueId/wine-list/:wineListId/import/confirm
   */
  confirmImport: async (
    venueId: string,
    wineListId: string,
    items: ImportPreviewItem[],
  ): Promise<ImportResult> => {
    const response = await api.post(
      `${importBase(venueId, wineListId)}/confirm`,
      { items },
    );
    return normalizeImportResult(response.data);
  },
};

export default wineListImportService;
